import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, Trash2, X, Loader2, Download, Pencil, Copy, Flag, Lightbulb, Crosshair, Power, Barcode, Printer } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useAutoSave } from "../hooks/useAutoSave";
import SaveStatus from "./SaveStatus";
import { downloadBayLabelsPdf, SLOT_LABEL_STYLES, type SlotLabelStyle } from "./helpers/printAgent";
import SortingLabelPrintModal from "./SortingLabelPrintModal";
import { apiFetch } from "../api/apiFetch";
import { useWarehouseScope } from "../access/useWarehouseScope";

// Bay + grid configuration for one sorting station — Pick & Sort, Put Wall,
// Sorter 1 or Sorter 2. Pick & Sort uses a fixed A–D × 1–6 grid with
// server-minted barcodes; the Put Wall is the same editable grid 18 positions
// wide (Pembroke's 72-slot wall, PW-prefixed barcodes). Sorter 1/2 instead MAP
// THE REAL EXISTING location codes (e.g. NPS201A1): the grid is seeded from
// those codes and each slot's barcode IS the scannable location string, so the
// moving head can be aimed at it during a live scan. Shared light config
// (colours, agents, brightness) lives in SortingSharedLighting, one level up.

type Station = "pick_and_sort" | "put_wall" | "sorter_1" | "sorter_2";

// Pick & Sort bays are four shelves. E was offered but never used on any bay,
// so it only showed as an empty row; a new bay should come up A–D too. Sorter 1
// and Sorter 2 are unaffected — their grids derive their axes from the real
// location codes, and three Sorter 2 bays DO still use E.
const SHELVES = ["A", "B", "C", "D"] as const;
const POSITIONS = [1, 2, 3, 4, 5, 6] as const;
// The put wall is the same four shelves, 18 positions across (three physical
// 6-wide bays joined — the group rule after every 6th column lands on the
// joins).
const PUT_WALL_POSITIONS = Array.from({ length: 18 }, (_, i) => i + 1) as readonly number[];

// Put wall default priority: same idea as the P&S four-shelf map — top shelf
// (D) cheapest, bottom (A) dearest, and within a shelf centre-out from the
// wall's middle (positions 9/10) — but generated, since a hand-written map
// for 72 cells would only obscure the rule.
const PUT_WALL_DEFAULT_PRIORITY: Record<string, number> = (() => {
    const out: Record<string, number> = {};
    const shelfOrder = [...SHELVES].reverse();
    const positionsCentreOut = [...PUT_WALL_POSITIONS].sort((a, b) =>
        Math.abs(a - 9.5) - Math.abs(b - 9.5) || a - b);
    let n = 1;
    for (const shelf of shelfOrder) {
        for (const position of positionsCentreOut) out[`${shelf}${position}`] = n++;
    }
    return out;
})();

// Pick & Sort default priority per slot. Position is centre-out (the operator
// stands mid-bay, so columns 3/4 are cheapest and 1/6 dearest); shelf runs from
// the top letter down, which is the order 9.0M sorter scans actually measured —
// the bottom shelf is the dearest in every bay shape tested, not the middle one.
//
// IMPORTANT: the shelf gradient depends on how many shelves the bay has, so one
// map cannot serve every shape. Dropping the absent shelves out of the A–E map
// gives the wrong answer for a four-shelf bay, because C and D trade places
// once E is gone. Pick the map that matches the bay.
const DEFAULT_PRIORITY_ABCDE: Record<string, number> = {
    E3: 1, E4: 2, E2: 3, E5: 4, E1: 5, E6: 6,
    D3: 7, D4: 8, C3: 9, C4: 10, D2: 11, D5: 12,
    C2: 13, C5: 14, D1: 15, D6: 16, C1: 17, C6: 18,
    A3: 19, A4: 20, B3: 21, B4: 22, A2: 23, A5: 24,
    B2: 25, B5: 26, A1: 27, A6: 28, B1: 29, B6: 30,
};
const DEFAULT_PRIORITY_ABCD: Record<string, number> = {
    D3: 1, D4: 2, D2: 3, D5: 4, C3: 5, C4: 6,
    D1: 7, D6: 8, C2: 9, C5: 10, B3: 11, B4: 12,
    A3: 13, A4: 14, C1: 15, C6: 16, B2: 17, B5: 18,
    A2: 19, A5: 20, B1: 21, B6: 22, A1: 23, A6: 24,
};
// A bay that has no E shelf takes the four-shelf map; anything else takes the
// five-shelf one. Cells the bay doesn't have simply never get enabled.
const defaultPriorityFor = (shelves: readonly string[]): Record<string, number> =>
    shelves.includes("E") ? DEFAULT_PRIORITY_ABCDE : DEFAULT_PRIORITY_ABCD;

// Fallback palette for swatches until the global one loads (read-only here;
// it's edited in SortingSharedLighting).
// Mirrors the long-standing Sorter 1 / Sorter 2 scheme in zonestorage.css
// (.letter-A … .letter-E), so the printed labels match both the scanner screen
// and the labels already on the racking. Text colour is not fixed — the label
// picks whichever of black/white contrasts more (readableTextOn).
const DEFAULT_SHELF_COLOURS: Record<string, string> = {
    A: "#3C89F0", B: "#3CB44B", C: "#FFE119", D: "#F58231", E: "#911EB4",
};
// Mirrors .number-1 … .number-6 in zonestorage.css. That CSS already repeats
// every 6 (its selectors list n, n+6, n+12), which is the same cycle
// positionColourKey applies here.
const DEFAULT_POSITION_COLOURS: Record<string, string> = {
    "1": "#808080", "2": "#000000", "3": "#A67C52",
    "4": "#BFEF45", "5": "#42D4F4", "6": "#F032E6",
};

// Number colours repeat every 6, so 1/7/13 share a swatch. Newport Sorter 2
// bay 7 is three physically separate bays of 6 wrapped round the operator, so
// the cycle resets once per bay and only six colours are ever in view. A no-op
// for every other bay — nothing else uses a position above 6.
const POSITION_COLOUR_CYCLE = 6;
const positionColourKey = (position: number) => String(((position - 1) % POSITION_COLOUR_CYCLE) + 1);

// Heavier rule after every 6th column of the grid, so a wide bay reads as
// groups instead of one undifferentiated run. It lines up with the repeating
// 1–6 number colours, and on the Newport put wall it falls exactly on the
// joins between its three physical bays. Never drawn on the last column —
// that edge is the table border.
const groupEndClass = (index: number, total: number) =>
    (index + 1) % POSITION_COLOUR_CYCLE === 0 && index + 1 < total ? " pas-grid-group-end" : "";

type LightColours = { active: string; complete: string };
type Palette = { shelf: Record<string, string>; position: Record<string, string>; light: LightColours };

// Pan/Tilt are 16-bit (0–65535), Zoom 8-bit (0–255). Operators enter 0–100%
// with one decimal (0.1% ≈ 0.5° of pan on a 540° head) — whole percents are
// too coarse to centre a beam on a slot.
const pctToDmx = (pct: number, max: number) => Math.max(0, Math.min(max, Math.round((Number(pct) || 0) / 100 * max)));
const dmxToPct = (dmx: number, max: number) => Math.round((Number(dmx) || 0) / max * 1000) / 10;

// One nudge = one visible display step: 0.1% for 16-bit pan/tilt, one DMX step
// (≈0.4%) for 8-bit zoom. Anything finer than a whole DMX step rounds back to
// the same value — the native spinner arrows on zoom looked dead for exactly
// that reason, so the % input's step must match too.
const DMX_NUDGE: Record<"pan" | "tilt" | "zoom", number> = { pan: 66, tilt: 66, zoom: 1 };
const PCT_STEP: Record<"pan" | "tilt" | "zoom", number> = { pan: 0.1, tilt: 0.1, zoom: 0.4 };

// Touch-friendly − / + stepper. Tap = one step; press-and-hold repeats (short
// delay, then ~12 steps/s) so bigger moves don't need dozens of taps. Pointer
// events cover mouse and touch alike; number-input spinners don't exist on
// mobile, which is what these buttons are for.
const NudgeButton = ({ dir, onStep, label }: { dir: 1 | -1; onStep: () => void; label: string }) => {
    const timers = useRef<{ delay?: number; repeat?: number }>({});
    const stop = () => { window.clearTimeout(timers.current.delay); window.clearInterval(timers.current.repeat); };
    useEffect(() => stop, []);
    return (
        <button
            type="button" aria-label={label} title={label}
            onPointerDown={e => {
                e.preventDefault(); // keep focus off the button so the input stays editable
                onStep();
                stop();
                timers.current.delay = window.setTimeout(() => { timers.current.repeat = window.setInterval(onStep, 80); }, 400);
            }}
            onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop}
            onContextMenu={e => e.preventDefault()} // long-press must repeat, not open a menu
            style={{
                width: 24, height: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
                border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 5, background: "transparent",
                color: "var(--color-text-muted, #6b7280)", cursor: "pointer", touchAction: "manipulation", flexShrink: 0,
            }}
        >
            {dir > 0 ? <Plus size={12} /> : <Minus size={12} />}
        </button>
    );
};

// Barcode edits are buffered locally and committed on blur / Enter, so the
// grid auto-save never posts a half-typed code (which would permanently
// rename the slot's scannable value mid-keystroke).
const BarcodeInput = ({ value, editable, placeholder, title, onCommit }: {
    value: string; editable: boolean; placeholder?: string; title?: string; onCommit: (v: string) => void;
}) => {
    const [draft, setDraft] = useState(value);
    const focused = useRef(false);
    useEffect(() => { if (!focused.current) setDraft(value); }, [value]);
    return (
        <input
            type="text" value={draft} readOnly={!editable} placeholder={placeholder} title={title}
            onFocus={() => { focused.current = true; }}
            onChange={e => editable && setDraft(e.target.value)}
            onBlur={() => { focused.current = false; onCommit(draft); }}
            onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
            style={{
                width: 92, fontSize: 10, textAlign: "center", fontFamily: "monospace",
                color: editable ? undefined : "var(--color-text-muted, #6b7280)",
                background: editable ? undefined : "transparent",
            }}
        />
    );
};

type Location = {
    location_id: number;
    bay_id: number;
    barcode: string;
    shelf: string;
    position: number;
    label: string;
    shelf_colour: string | null;
    position_colour: string | null;
    priority: number;
    // Double-bay Large-wave fill order (compared ACROSS both bays);
    // null = fall back to `priority` at sort time.
    large_priority?: number | null;
    pan: number;
    tilt: number;
    zoom: number;
};
type Bay = {
    bay_id: number; warehouse: string; name: string; station?: Station; sorter_bay_no?: number | null;
    locations: Location[];
    light_enabled?: boolean; artnet_host?: string | null; artnet_universe?: number; dmx_start_channel?: number;
};

// One editable grid cell. Keyed by "A1" for Pick & Sort, or by the full
// location barcode for Sorter 1/2.
// `barcode` — the slot's scannable value. P&S: editable (reuse a physically
// printed code); "" on a never-saved slot means the server mints one. Sorter
// 1/2: the REAL location code the operator scans — view-only.
type Cell = { enabled: boolean; priority: number; largePriority: number | null; pan: number; tilt: number; zoom: number; barcode: string };

const SortingBayManager = ({ warehouses: allWarehouses, station = "pick_and_sort" }: { warehouses: string[]; station?: Station }) => {
    const { t } = useTranslation();
    // Without cross_warehouse only the person's own warehouse is offered.
    const { canCross, own } = useWarehouseScope();
    const warehouses = useMemo(() => (canCross ? allWarehouses : allWarehouses.filter(w => w === own)), [canCross, own, allWarehouses]);
    // Genuinely Pick-&-Sort-only bits: the Large (double-bay) priority mode
    // and the points-target hint. Everything about EDITING the grid — cell
    // toggles, barcode auto-mint/override, priorities, duplicate — is shared
    // with the put wall, which is the same kind of admin-defined bay.
    const isPas = station === "pick_and_sort";
    const hasEditableGrid = station === "pick_and_sort" || station === "put_wall";
    const isSorter2 = station === "sorter_2";
    // The put wall is managed as just another bay under Pick & Sort (the
    // Pembroke tab's "Bay 3 (Putwall)"), so its geometry is a property of
    // the BAY, not of the manager: a put_wall bay row is 1–18 wide, every
    // other editable bay 1–6. The BE keys the row's `station` = 'put_wall',
    // which is what routes put-wall waves to it and keeps it out of the
    // Large bay pair.
    const isPutWallBay = (bay: Bay) => bay.station === "put_wall";
    const positionsForBay = (bay: Bay): readonly number[] => (isPutWallBay(bay) ? PUT_WALL_POSITIONS : POSITIONS);

    const [warehouse, setWarehouse] = useState<string>(warehouses[0] ?? "");
    const [bays, setBays] = useState<Bay[]>([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState("");
    const [newBayNo, setNewBayNo] = useState("");
    // Add Bay under Pick & Sort: tick to create the bay as the put wall
    // (station 'put_wall', 4 shelves × 18) instead of a normal 6-wide bay.
    const [newPutWall, setNewPutWall] = useState(false);
    const [renaming, setRenaming] = useState<Bay | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const [duplicating, setDuplicating] = useState<Bay | null>(null);
    const [duplicateValue, setDuplicateValue] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [editingBayId, setEditingBayId] = useState<number | null>(null);
    // Which fill order the grid's priority inputs edit (P&S only).
    const [priorityMode, setPriorityMode] = useState<"small" | "large">("small");
    const [grid, setGrid] = useState<Record<string, Cell>>({});
    const [savedGrid, setSavedGrid] = useState<Record<string, Cell>>({});
    const [showBarcodes, setShowBarcodes] = useState(false);
    const [savingGrid, setSavingGrid] = useState(false);
    // Sorter only: the real existing location barcodes for the bay being edited.
    const [sorterLocs, setSorterLocs] = useState<string[]>([]);
    const [seedingGrid, setSeedingGrid] = useState(false);
    // Palette is loaded read-only for swatches + label PDFs (edited elsewhere).
    const [palette, setPalette] = useState<Palette>({ shelf: { ...DEFAULT_SHELF_COLOURS }, position: { ...DEFAULT_POSITION_COLOURS }, light: { active: "Green", complete: "Blue" } });
    const [aimingKey, setAimingKey] = useState<string | null>(null);
    const [editingLightBayId, setEditingLightBayId] = useState<number | null>(null);
    const [lightDraft, setLightDraft] = useState<{ light_enabled: boolean; artnet_host: string; artnet_universe: number; dmx_start_channel: number }>({ light_enabled: false, artnet_host: "", artnet_universe: 0, dmx_start_channel: 1 });
    const [savedLightDraft, setSavedLightDraft] = useState<typeof lightDraft | null>(null);
    const [savingLight, setSavingLight] = useState(false);

    const gridDirty = JSON.stringify(grid) !== JSON.stringify(savedGrid);
    const lightDirty = savedLightDraft != null && JSON.stringify(lightDraft) !== JSON.stringify(savedLightDraft);
    const [downloadingBay, setDownloadingBay] = useState<number | null>(null);
    // Which bay's download menu is open — the Download icon offers every label
    // design, not just the standard one, so a site can sheet out whichever
    // stock it has loaded. The menu is FIXED-positioned at the button's screen
    // rect (captured on open) rather than absolute inside the bay card, whose
    // ancestors clip overflow and were cutting the list off after one row.
    const [downloadMenu, setDownloadMenu] = useState<{ bayId: number; top: number; right: number } | null>(null);
    // Close it on Escape like a native menu; outside clicks are caught by the
    // backdrop rendered behind it.
    useEffect(() => {
        if (downloadMenu == null) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setDownloadMenu(null); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [downloadMenu]);
    // Bay whose labels the print dialog is open for.
    const [printBay, setPrintBay] = useState<Bay | null>(null);
    const [removeConfirm, setRemoveConfirm] = useState<Bay | null>(null);

    const refresh = useCallback(async () => {
        if (!warehouse) { setBays([]); setLoading(false); return; }
        setLoading(true);
        try {
            // Sorter 1/2: inherit any existing bays from the live location data
            // (idempotent) so they appear without the admin recreating them.
            if (!hasEditableGrid) {
                await apiFetch(`/pick-and-sort-admin/bays/import`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ warehouse, station }),
                }).catch(() => { /* best-effort; list still loads */ });
            }
            const loadStation = async (s: Station): Promise<Bay[]> => {
                const res = await apiFetch(`/pick-and-sort-admin/bays?warehouse=${encodeURIComponent(warehouse)}&station=${s}`, {});
                if (!res.ok) throw new Error("load");
                const data = await res.json();
                return Array.isArray(data.bays) ? data.bays : [];
            };
            // Pick & Sort also lists the warehouse's put wall bay(s) — one
            // merged list, Pick & Sort bays first (in the BE's position
            // order), then the wall. The BE stamps `station` on every row,
            // so the rows keep telling us which is which; a missing field
            // on an older BE defaults to the list it came from.
            if (isPas) {
                const [pas, wall] = await Promise.all([
                    loadStation("pick_and_sort"),
                    loadStation("put_wall").catch(() => [] as Bay[]),
                ]);
                setBays([
                    ...pas.map(b => ({ ...b, station: b.station ?? "pick_and_sort" })),
                    ...wall.map(b => ({ ...b, station: b.station ?? "put_wall" })),
                ]);
            } else {
                setBays(await loadStation(station));
            }
        } catch {
            toast.error(t("server_error"), { toastId: "pas-load-err" });
        } finally {
            setLoading(false);
        }
    }, [warehouse, station, hasEditableGrid, isPas, t]);

    useEffect(() => { refresh(); }, [refresh]);

    // Load the global label palette once (read-only — for swatches + PDFs).
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/pick-and-sort-admin/colours`, {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive && data?.shelf && data?.position) {
                    setPalette({ shelf: data.shelf, position: data.position, light: data.light ?? { active: "Green", complete: "Blue" } });
                }
            } catch { /* keep defaults */ }
        })();
        return () => { alive = false; };
    }, []);

    // Default / re-sync the selected warehouse as the list loads in.
    useEffect(() => {
        if ((!warehouse || !warehouses.includes(warehouse)) && warehouses.length) setWarehouse(warehouses[0]);
    }, [warehouses, warehouse]);

    // Per-warehouse label stock for the slot labels. All three stations print
    // the same label, so one size per warehouse covers the lot. null = the
    // built-in 185×35mm, which is what these were fixed at before.
    const [labelSize, setLabelSize] = useState<{ w: number | null; h: number | null }>({ w: null, h: null });
    const readLabelSize = (d: Record<string, unknown>) => ({
        w: Number(d.sorting_label_width_mm) > 0 ? Number(d.sorting_label_width_mm) : null,
        h: Number(d.sorting_label_height_mm) > 0 ? Number(d.sorting_label_height_mm) : null,
    });
    useEffect(() => {
        if (!warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/settings/${encodeURIComponent(warehouse)}`, {});
                if (!res.ok || !alive) return;
                setLabelSize(readLabelSize(await res.json()));
            } catch { /* keep the built-in default */ }
        })();
        return () => { alive = false; };
    }, [warehouse]);

    const saveLabelSize = async (w: number | null, h: number | null): Promise<boolean | "forbidden"> => {
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(warehouse)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sorting_label_width_mm: w, sorting_label_height_mm: h }),
            });
            if (res.status === 403) return "forbidden";
            if (!res.ok) return false;
            setLabelSize(readLabelSize(await res.json()));
            return true;
        } catch { return false; }
    };

    const cellKey = (shelf: string, position: number) => `${shelf}${position}`;
    // Sorter slot identity is the full barcode; shelf/position are its trailing
    // shelf letter + 1–2 digits ("…A1" → A/1, "…E18" → E/18). Not the last two
    // characters: Newport Sorter 2 bay 7 runs to position 18, and "NPS207A18"
    // read that way is shelf "1", position 8.
    const SLOT_RE = /([A-E])(\d{1,2})$/;
    const shelfOf = (barcode: string) => SLOT_RE.exec(barcode)?.[1] ?? "";
    const positionOf = (barcode: string) => Number(SLOT_RE.exec(barcode)?.[2] ?? NaN);

    // Open the grid editor for a bay — hydrate cells from its existing locations.
    const openEditor = async (bay: Bay) => {
        setShowBarcodes(false);
        setPriorityMode("small");
        if (hasEditableGrid) {
            const nextGrid: Record<string, Cell> = {};
            // Shape the defaults to the bay it already is, so a four-shelf bay
            // doesn't inherit the five-shelf shelf order.
            const defaults = isPutWallBay(bay)
                ? PUT_WALL_DEFAULT_PRIORITY
                : defaultPriorityFor([...new Set(bay.locations.map(l => l.shelf))]);
            for (const shelf of SHELVES) {
                for (const position of positionsForBay(bay)) {
                    const key = cellKey(shelf, position);
                    nextGrid[key] = { enabled: false, priority: defaults[key] ?? 999, largePriority: null, pan: 32768, tilt: 32768, zoom: 128, barcode: "" };
                }
            }
            for (const loc of bay.locations) {
                // Ignore any slot on a shelf the grid no longer offers (i.e. a
                // legacy E). Keeping it would hydrate a cell that never renders,
                // which the save then posts straight back — invisible and
                // impossible to clear. Dropping it lets the next save retire it.
                if (!SHELVES.includes(loc.shelf as typeof SHELVES[number])) continue;
                nextGrid[cellKey(loc.shelf, loc.position)] = {
                    enabled: true, priority: loc.priority,
                    largePriority: loc.large_priority ?? null,
                    pan: loc.pan ?? 32768, tilt: loc.tilt ?? 32768, zoom: loc.zoom ?? 128,
                    barcode: loc.barcode ?? "",
                };
            }
            setSorterLocs([]);
            setGrid(nextGrid);
            setSavedGrid(nextGrid);
            setEditingBayId(bay.bay_id);
            return;
        }
        // Sorter: seed the grid from the real existing location codes.
        setEditingBayId(bay.bay_id);
        setSeedingGrid(true);
        try {
            const qs = new URLSearchParams({ warehouse, station });
            if (bay.sorter_bay_no != null) qs.set("sorter_bay_no", String(bay.sorter_bay_no));
            const res = await apiFetch(`/pick-and-sort-admin/sorter-locations?${qs.toString()}`, {});
            const data = res.ok ? await res.json() : { locations: [] };
            const locs: string[] = Array.isArray(data.locations) ? data.locations : [];
            const byBarcode = new Map(bay.locations.map(l => [l.barcode, l]));
            const nextGrid: Record<string, Cell> = {};
            locs.forEach((bc, i) => {
                const ex = byBarcode.get(bc);
                nextGrid[bc] = ex
                    ? { enabled: true, priority: ex.priority, largePriority: null, pan: ex.pan ?? 32768, tilt: ex.tilt ?? 32768, zoom: ex.zoom ?? 128, barcode: bc }
                    : { enabled: false, priority: i + 1, largePriority: null, pan: 32768, tilt: 32768, zoom: 128, barcode: bc };
            });
            // Defensive: keep any already-configured slot even if the seed misses it.
            for (const l of bay.locations) {
                if (!nextGrid[l.barcode]) nextGrid[l.barcode] = { enabled: true, priority: l.priority, largePriority: null, pan: l.pan ?? 32768, tilt: l.tilt ?? 32768, zoom: l.zoom ?? 128, barcode: l.barcode };
            }
            // Shelf letter, then position as a NUMBER — a plain .sort() gives
            // A1, A10, A11, … A2 once a bay runs past position 9, and the
            // fallback priorities seeded below follow this order.
            const allBarcodes = [...new Set([...locs, ...bay.locations.map(l => l.barcode)])]
                .sort((a, b) => shelfOf(a).localeCompare(shelfOf(b)) || positionOf(a) - positionOf(b) || a.localeCompare(b));
            setSorterLocs(allBarcodes);
            setGrid(nextGrid);
            setSavedGrid(nextGrid);
        } catch {
            toast.error(t("server_error"), { toastId: "pas-seed-err" });
            setSorterLocs([]);
        } finally {
            setSeedingGrid(false);
        }
    };

    const toggleCell = (key: string) => setGrid(g => ({ ...g, [key]: { ...g[key], enabled: !g[key].enabled } }));
    const setCellPriority = (key: string, value: string) => setGrid(g => ({ ...g, [key]: { ...g[key], priority: Number(value) || 1 } }));
    // Blank clears the Large priority (slot falls back to `priority`).
    const setCellLargePriority = (key: string, value: string) =>
        setGrid(g => ({ ...g, [key]: { ...g[key], largePriority: value.trim() === "" ? null : (Number(value) || 1) } }));
    // Barcodes are case-significant — store exactly what's typed (trim on save).
    const setCellBarcode = (key: string, value: string) => setGrid(g => ({ ...g, [key]: { ...g[key], barcode: value } }));
    // Aim fields are entered as 0–100% and stored as raw DMX.
    const setCellAim = (key: string, field: "pan" | "tilt" | "zoom", pct: string) => {
        const max = field === "zoom" ? 255 : 65535;
        setGrid(g => ({ ...g, [key]: { ...g[key], [field]: pctToDmx(Number(pct), max) } }));
    };
    // Stepper buttons adjust the raw DMX directly so every tap moves the head,
    // regardless of how the percentage rounds. `mult` scales the step — held
    // arrow keys pass an accelerating multiplier for long throws.
    const nudgeCellAim = (key: string, field: "pan" | "tilt" | "zoom", dir: 1 | -1, mult: number = 1) => {
        const max = field === "zoom" ? 255 : 65535;
        setGrid(g => ({ ...g, [key]: { ...g[key], [field]: Math.max(0, Math.min(max, (g[key][field] ?? 0) + dir * DMX_NUDGE[field] * mult)) } }));
    };

    // Live-aim: push a bay's light to a given aim (or off) so the head moves
    // immediately while the operator tunes a slot. Best-effort.
    const pushPreview = useCallback(async (bayId: number, body: { pan?: number; tilt?: number; zoom?: number; off?: boolean }) => {
        try {
            await apiFetch(`/pick-and-sort-admin/light-preview`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ bay_id: bayId, ...body }),
            });
        } catch { /* preview is best-effort */ }
    }, []);

    const lightsOff = (bayId: number) => { setAimingKey(null); pushPreview(bayId, { off: true }); };

    // Throttle, NOT debounce: a held arrow key streams grid changes faster
    // than the old 120ms debounce could ever fire, so the head sat still
    // until the key was released. Push at most every 120ms but always push —
    // the beam follows the keys, and the trailing timer lands the final value.
    const lastAimPushRef = useRef(0);
    useEffect(() => {
        if (aimingKey == null || editingBayId == null) return;
        const cell = grid[aimingKey];
        if (!cell) return;
        const wait = Math.max(0, 120 - (Date.now() - lastAimPushRef.current));
        const id = setTimeout(() => {
            lastAimPushRef.current = Date.now();
            pushPreview(editingBayId, { pan: cell.pan, tilt: cell.tilt, zoom: cell.zoom });
        }, wait);
        return () => clearTimeout(id);
    }, [aimingKey, editingBayId, grid, pushPreview]);

    // Keyboard aiming while a slot is armed: ↑/↓ nudge pan, ←/→ nudge tilt,
    // Ctrl+↑/↓ nudge zoom. Held keys auto-repeat and ACCELERATE — fine steps
    // for the first moments, then bigger multiples for long throws. Typing in
    // an input, select or textarea is left alone.
    const aimHoldRef = useRef<{ combo: string | null; since: number }>({ combo: null, since: 0 });
    useEffect(() => {
        if (aimingKey == null) return;
        const onKeyDown = (e: KeyboardEvent) => {
            const el = e.target as HTMLElement | null;
            if (el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) return;
            let field: "pan" | "tilt" | "zoom" | null = null;
            let dir: 1 | -1 = 1;
            if (e.key === "ArrowUp") { field = e.ctrlKey ? "zoom" : "pan"; dir = 1; }
            else if (e.key === "ArrowDown") { field = e.ctrlKey ? "zoom" : "pan"; dir = -1; }
            else if (e.key === "ArrowRight") { field = "tilt"; dir = 1; }
            else if (e.key === "ArrowLeft") { field = "tilt"; dir = -1; }
            if (!field) return;
            e.preventDefault();
            const now = Date.now();
            const combo = `${e.key}|${e.ctrlKey ? 1 : 0}`;
            if (aimHoldRef.current.combo !== combo) aimHoldRef.current = { combo, since: now };
            const held = now - aimHoldRef.current.since;
            const mult = held < 400 ? 1 : held < 1200 ? 4 : held < 2500 ? 12 : 30;
            nudgeCellAim(aimingKey, field, dir, mult);
        };
        const onKeyUp = () => { aimHoldRef.current = { combo: null, since: 0 }; };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        return () => { window.removeEventListener("keydown", onKeyDown); window.removeEventListener("keyup", onKeyUp); };
        // nudgeCellAim writes through a functional setGrid, so the closure can't go stale.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aimingKey]);

    const openLightEditor = (bay: Bay) => {
        const draft = {
            light_enabled: !!bay.light_enabled,
            artnet_host: bay.artnet_host ?? "",
            artnet_universe: bay.artnet_universe ?? 0,
            dmx_start_channel: bay.dmx_start_channel ?? 1,
        };
        setLightDraft(draft);
        setSavedLightDraft(draft);
        setEditingLightBayId(bay.bay_id);
    };

    const handleSaveLight = async (bayId: number): Promise<boolean> => {
        const draft = lightDraft;
        setSavingLight(true);
        try {
            const res = await apiFetch(`/pick-and-sort-admin/bays/light`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ bay_id: bayId, ...draft }),
            });
            if (!res.ok) throw new Error("light");
            // Keep the panel open (auto-save) — update the bay row in place.
            setSavedLightDraft(draft);
            setBays(bs => bs.map(b => (b.bay_id === bayId ? { ...b, ...draft } : b)));
            return true;
        } catch {
            toast.error(t("server_error"), { toastId: "pas-light-err" });
            return false;
        } finally {
            setSavingLight(false);
        }
    };

    // Light config auto-save. Gated while the host field contains whitespace
    // (mid-edit) so the on-site agent never receives a mangled Art-Net host.
    useAutoSave(lightDraft, {
        dirty: lightDirty, saving: savingLight,
        enabled: editingLightBayId != null && !/\s/.test(lightDraft.artnet_host),
        save: () => handleSaveLight(editingLightBayId as number),
    });

    // Closing the panel flushes any change the debounce hasn't posted yet.
    const closeLightEditor = (bayId: number) => {
        if (lightDirty && !savingLight) void handleSaveLight(bayId);
        setEditingLightBayId(null);
    };

    const handleAdd = async () => {
        if (!newName.trim()) return;
        setSubmitting(true);
        try {
            // A put wall bay is created from the Pick & Sort tab with the
            // toggle ticked; the BE keys it station 'put_wall'.
            const body: Record<string, unknown> = { warehouse, name: newName.trim(), station: isPas && newPutWall ? "put_wall" : station };
            if (isSorter2 && newBayNo.trim()) body.sorter_bay_no = Number(newBayNo);
            const res = await apiFetch(`/pick-and-sort-admin/bays/add`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!res.ok) throw new Error("add");
            setAdding(false);
            setNewName("");
            setNewBayNo("");
            setNewPutWall(false);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "pas-add-err" });
        } finally {
            setSubmitting(false);
        }
    };

    const handleRename = async () => {
        if (!renaming || !renameValue.trim()) return;
        setSubmitting(true);
        try {
            const res = await apiFetch(`/pick-and-sort-admin/bays/update`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ bay_id: renaming.bay_id, name: renameValue.trim() }),
            });
            if (!res.ok) throw new Error("rename");
            setRenaming(null);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "pas-rename-err" });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDuplicate = async () => {
        if (!duplicating || !duplicateValue.trim()) return;
        setSubmitting(true);
        try {
            const res = await apiFetch(`/pick-and-sort-admin/bays/duplicate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ bay_id: duplicating.bay_id, name: duplicateValue.trim() }),
            });
            if (!res.ok) throw new Error("duplicate");
            setDuplicating(null);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "pas-duplicate-err" });
        } finally {
            setSubmitting(false);
        }
    };

    // Auto-saves while the editor stays open (returns false on failure so the
    // auto-save hook won't retry the identical payload).
    const handleSaveGrid = async (bayId: number): Promise<boolean> => {
        const snapshot = grid;
        // Barcodes must be unique — catch duplicates within this grid before
        // posting (the server re-checks globally across every warehouse + bay).
        const enabledCells = Object.entries(snapshot).filter(([, cell]) => cell.enabled);
        if (hasEditableGrid) {
            const seen = new Set<string>();
            for (const [, cell] of enabledCells) {
                const bc = cell.barcode.trim();
                if (!bc) continue;
                if (seen.has(bc)) {
                    toast.error(`${t("pas_barcode_duplicate")}: ${bc}`, { toastId: "pas-save-err" });
                    return false;
                }
                seen.add(bc);
            }
        }
        setSavingGrid(true);
        try {
            // Pick & Sort sends shelf/position (+ optional custom barcode; blank →
            // the server mints one); Sorter sends the real location barcode
            // (server derives shelf/position).
            const locations = enabledCells.map(([key, cell]) => {
                if (hasEditableGrid) {
                    const shelf = key[0];
                    const position = Number(key.slice(1));
                    return {
                        shelf, position, label: `${shelf}${position}`, priority: cell.priority,
                        large_priority: cell.largePriority,
                        pan: cell.pan, tilt: cell.tilt, zoom: cell.zoom,
                        barcode: cell.barcode.trim() || undefined,
                    };
                }
                // No priority for sorters — the server derives a stable one.
                return { barcode: key, pan: cell.pan, tilt: cell.tilt, zoom: cell.zoom };
            });
            const res = await apiFetch(`/pick-and-sort-admin/locations/save-grid`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ bay_id: bayId, locations }),
            });
            if (!res.ok) {
                // Surface the server's reason (e.g. which barcode collides and
                // where) instead of a generic failure.
                const data = await res.json().catch(() => null);
                toast.error(data?.message || t("server_error"), { toastId: "pas-save-err" });
                return false;
            }
            const data = await res.json().catch(() => null);
            const locs: Location[] = Array.isArray(data?.locations) ? data.locations : [];
            // Reflect the saved state without a full refresh (no list flicker,
            // editor stays open): update the bay row in place, and pull freshly
            // minted barcodes back into the editor so "auto" slots show theirs.
            setBays(bs => bs.map(b => (b.bay_id === bayId ? { ...b, locations: locs } : b)));
            if (hasEditableGrid) {
                const byKey = new Map(locs.map(l => [cellKey(l.shelf, l.position), l]));
                const withServerBarcodes = (g: Record<string, Cell>): Record<string, Cell> =>
                    Object.fromEntries(Object.entries(g).map(([k, c]) => {
                        const l = byKey.get(k);
                        return [k, l && c.enabled ? { ...c, barcode: l.barcode } : c];
                    }));
                setGrid(g => withServerBarcodes(g));
                setSavedGrid(withServerBarcodes(snapshot));
            } else {
                setSavedGrid(snapshot);
            }
            return true;
        } catch {
            toast.error(t("server_error"), { toastId: "pas-save-err" });
            return false;
        } finally {
            setSavingGrid(false);
        }
    };

    // Grid auto-save: fires once edits settle; disabled while a sorter grid is
    // still seeding or no editor is open.
    useAutoSave(grid, {
        dirty: gridDirty, saving: savingGrid,
        enabled: editingBayId != null && !seedingGrid,
        save: () => handleSaveGrid(editingBayId as number),
    });

    // Closing the editor flushes any change the debounce hasn't posted yet.
    const closeGridEditor = (bayId: number) => {
        if (gridDirty && !savingGrid) void handleSaveGrid(bayId);
        lightsOff(bayId);
        setEditingBayId(null);
    };

    const handleDownloadBay = async (bay: Bay, style: SlotLabelStyle) => {
        if (bay.locations.length === 0) return;
        setDownloadMenu(null);
        setDownloadingBay(bay.bay_id);
        try {
            const result = await downloadBayLabelsPdf(
                bay.locations.map(l => ({
                    barcode: l.barcode, label: l.label, shelf: l.shelf, position: l.position,
                    shelf_colour: l.shelf_colour ?? palette.shelf[l.shelf],
                    position_colour: l.position_colour ?? palette.position[positionColourKey(l.position)],
                })),
                // The style in the filename tells apart a folder of downloads;
                // the standard design keeps its old name so nothing re-saved
                // turns into a duplicate.
                style === "standard"
                    ? `${station}-${warehouse}-${bay.name}.pdf`
                    : `${station}-${warehouse}-${bay.name}-${style}.pdf`,
                // Same stock the Print button uses (standard design only — the
                // rest fix their own size), so a downloaded sheet and a printed
                // run come out identical.
                { widthMm: labelSize.w, heightMm: labelSize.h },
                style,
            );
            if (!result.success) toast.error(result.error || t("download_failed"), { toastId: "pas-pdf-err" });
        } finally {
            setDownloadingBay(null);
        }
    };

    const confirmRemove = async () => {
        if (!removeConfirm) return;
        setSubmitting(true);
        try {
            const res = await apiFetch(`/pick-and-sort-admin/bays/archive`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ bay_id: removeConfirm.bay_id }),
            });
            if (!res.ok) throw new Error("remove");
            setRemoveConfirm(null);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "pas-remove-err" });
        } finally {
            setSubmitting(false);
        }
    };

    // Grid axes — fixed A–E × 1–6 for P&S; derived from the seeded barcodes for
    // sorters (so the grid mirrors exactly the real slots that exist).
    // A barcode that doesn't end in shelf+position yields "" / NaN — drop those
    // rather than render a blank axis entry the operator can't map to a slot.
    const sorterShelves = [...new Set(sorterLocs.map(shelfOf))].filter(Boolean).sort();
    const sorterPositions = [...new Set(sorterLocs.map(positionOf))].filter(Number.isFinite).sort((a, b) => a - b);
    const sorterBarcodeAt = (shelf: string, position: number) =>
        sorterLocs.find(b => shelfOf(b) === shelf && positionOf(b) === position);

    const renderCellControls = (key: string, cell: Cell, lightEnabled: boolean) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
            <div style={{ display: "flex", gap: 2 }}>
                <span style={{ width: 16, height: 16, borderRadius: 3, background: palette.shelf[shelfOfKey(key)], display: "inline-block" }} />
                <span style={{ width: 16, height: 16, borderRadius: 3, background: palette.position[positionColourKey(positionOfKey(key))], display: "inline-block" }} />
            </div>
            <input type="checkbox" checked={cell.enabled} onChange={() => toggleCell(key)} title={t("pas_enable_slot")} />
            {/* Slot barcode — editable for P&S (reuse a physically printed code);
                read-only for sorters, whose barcode IS the scanned location string. */}
            {showBarcodes && cell.enabled && (
                <BarcodeInput
                    value={cell.barcode}
                    editable={hasEditableGrid}
                    placeholder={hasEditableGrid ? t("pas_barcode_auto") : undefined}
                    title={hasEditableGrid ? t("pas_barcode") : t("pas_barcode_sorter_hint")}
                    onCommit={v => setCellBarcode(key, v)}
                />
            )}
            {/* Priority is only admin-set for Pick & Sort; Sorter 1/2 sort order is
                owned by the old BE + Mongo, so no priority field there. The
                Small/Large mode toggle above the grid switches which fill
                order the input edits: `priority` (one bay) or
                `large_priority` (compared across the bay PAIR). */}
            {cell.enabled && hasEditableGrid && (
                <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontSize: 11, width: "100%" }}>
                    <span
                        title={priorityMode === "large" ? t("pas_priority_mode_large") : t("pas_priority")}
                        aria-label={priorityMode === "large" ? t("pas_priority_mode_large") : t("pas_priority")}
                        style={{ display: "inline-flex", color: priorityMode === "large" ? "var(--color-primary, #2563eb)" : "var(--color-text-muted, #6b7280)" }}
                    >
                        <Flag size={13} />
                    </span>
                    {priorityMode === "large" ? (
                        <input
                            style={{ width: 48, fontSize: 12, textAlign: "center" }}
                            type="number" min={1}
                            value={cell.largePriority ?? ""}
                            placeholder={String(cell.priority)}
                            onChange={e => setCellLargePriority(key, e.target.value)}
                            title={t("pas_priority_mode_large")}
                        />
                    ) : (
                        <input style={{ width: 48, fontSize: 12, textAlign: "center" }} type="number" min={1} value={cell.priority} onChange={e => setCellPriority(key, e.target.value)} title={t("pas_priority")} />
                    )}
                </label>
            )}
            {cell.enabled && lightEnabled && (
                <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2, borderTop: "1px solid var(--color-border, #e5e7eb)", paddingTop: 2 }}>
                    <button
                        type="button"
                        aria-label={t("pas_aim")}
                        title={t("pas_aim_hint")}
                        onClick={() => {
                            // Un-arming must also BLACK OUT the head — leaving the
                            // last lit preview burning until the dwell expires reads
                            // as "the off button doesn't work" on the floor.
                            if (aimingKey === key) {
                                if (editingBayId != null) lightsOff(editingBayId); else setAimingKey(null);
                            } else setAimingKey(key);
                        }}
                        style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 20, height: 20, padding: 0, alignSelf: "center", cursor: "pointer",
                            border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 4,
                            background: aimingKey === key ? "var(--color-primary, #2563eb)" : "transparent",
                            color: aimingKey === key ? "#fff" : "var(--color-text-muted, #6b7280)",
                        }}
                    >
                        <Crosshair size={12} />
                    </button>
                    {([["pan", "P"], ["tilt", "T"], ["zoom", "Z"]] as const).map(([field, lbl]) => (
                        <div key={field} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 3, fontSize: 10 }}>
                            <span title={t(`pas_${field}` as any)} style={{ color: "var(--color-text-muted, #6b7280)", width: 10 }}>{lbl}</span>
                            <NudgeButton dir={-1} label={`${lbl} −`} onStep={() => nudgeCellAim(key, field, -1)} />
                            <input style={{ width: 48, fontSize: 11, textAlign: "center" }} type="number" min={0} max={100} step={PCT_STEP[field]}
                                value={dmxToPct(cell[field], field === "zoom" ? 255 : 65535)}
                                onChange={e => setCellAim(key, field, e.target.value)} title={t(`pas_${field}` as any)} />
                            <NudgeButton dir={1} label={`${lbl} +`} onStep={() => nudgeCellAim(key, field, 1)} />
                            <span style={{ color: "var(--color-text-muted, #6b7280)" }}>%</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // Key → shelf/position. P&S keys are "A1"; sorter keys are full barcodes.
    function shelfOfKey(key: string) { return hasEditableGrid ? key[0] : shelfOf(key); }
    function positionOfKey(key: string) { return hasEditableGrid ? Number(key.slice(1)) : positionOf(key); }

    return (
        <div>
            <div className="filter-pills" role="tablist" style={{ marginBottom: 12 }}>
                {warehouses.map(w => (
                    <button
                        key={w}
                        type="button"
                        role="tab"
                        aria-selected={warehouse === w}
                        className={`filter-pill${warehouse === w ? " filter-pill--active" : ""}`}
                        onClick={() => setWarehouse(w)}
                    >
                        {w}
                    </button>
                ))}
            </div>

            {/* The Pick & Sort time target moved to Warehouse > Points, where every
                department's target now lives. Same pick_and_sort_timer_ms
                column: it still drives the moving-head light's hurry-up cue as
                well as the score, so the settings PATCH still evicts the
                pas-light cache. */}
            {isPas && (
                <p className="admin-settings-section-hint" style={{ marginBottom: 12 }}>
                    {t("targets_moved_to_points")}{" "}
                    <Link to="/admin/warehouse/points">{t("points_settings_title")}</Link>
                </p>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                <button className="btn btn-primary btn-sm" onClick={() => { setAdding(true); setNewName(""); setNewBayNo(""); setNewPutWall(false); }}>
                    <Plus size={14} /> {t("pas_add_bay")}
                </button>
            </div>

            {loading && <div className="admin-settings-loading">{t("loading")}</div>}
            {!loading && bays.length === 0 && <div className="admin-settings-empty">{t("pas_no_bays")}</div>}

            {!loading && bays.map(bay => (
                <div key={bay.bay_id} style={{ marginBottom: 12, border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 8, padding: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <strong>{bay.name}</strong>
                            {bay.sorter_bay_no != null && <span style={{ color: "var(--color-text-muted, #6b7280)", fontSize: 12 }}>#{bay.sorter_bay_no}</span>}
                            {isPutWallBay(bay) && (
                                <span className="pas-bay-tag" data-testid="pas-put-wall-tag" title={t("pas_add_bay_put_wall_hint")}>
                                    {t("pas_put_wall_badge")}
                                </span>
                            )}
                            <button
                                type="button"
                                className="dispatch-admin-row-icon"
                                title={t("pas_rename_bay")}
                                onClick={() => { setRenaming(bay); setRenameValue(bay.name); }}
                            >
                                <Pencil size={14} />
                            </button>
                        </span>
                        <span style={{ color: "var(--color-text-muted, #6b7280)", fontSize: 13 }}>
                            {t("pas_slot_count", { count: String(bay.locations.length) })}
                        </span>
                        <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => (editingBayId === bay.bay_id ? closeGridEditor(bay.bay_id) : openEditor(bay))}>
                                {editingBayId === bay.bay_id ? t("close") : t("pas_edit_grid")}
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => (editingLightBayId === bay.bay_id ? closeLightEditor(bay.bay_id) : openLightEditor(bay))}>
                                <Lightbulb size={14} /> {t("pas_light")}{bay.light_enabled ? " ✓" : ""}
                            </button>
                            {editingBayId === bay.bay_id && (
                                <button
                                    className={`btn btn-ghost btn-sm${showBarcodes ? " filter-pill--active" : ""}`}
                                    title={t("pas_barcodes_hint")}
                                    onClick={() => setShowBarcodes(v => !v)}
                                >
                                    <Barcode size={14} /> {t("pas_barcodes")}
                                </button>
                            )}
                            {/* Icon-only — the bay row already carries several
                                buttons and the labels are the obvious subject.
                                Opens a small menu of the label designs; each
                                downloads the bay as a PDF in that design. */}
                            <span style={{ display: "inline-flex" }}>
                                <button className="btn btn-ghost btn-sm" title={t("download_all")} aria-label={t("download_all")}
                                    aria-haspopup="menu" aria-expanded={downloadMenu?.bayId === bay.bay_id}
                                    disabled={bay.locations.length === 0 || downloadingBay === bay.bay_id}
                                    onClick={e => {
                                        const r = e.currentTarget.getBoundingClientRect();
                                        setDownloadMenu(m => (m?.bayId === bay.bay_id
                                            ? null
                                            : { bayId: bay.bay_id, top: r.bottom + 4, right: window.innerWidth - r.right }));
                                    }}>
                                    {downloadingBay === bay.bay_id ? <Loader2 size={14} className="dispatch-admin-spin" /> : <Download size={14} />}
                                </button>
                                {downloadMenu?.bayId === bay.bay_id && (<>
                                    <div style={{ position: "fixed", inset: 0, zIndex: 20 }} onClick={() => setDownloadMenu(null)} />
                                    <div role="menu" style={{
                                        position: "fixed", right: downloadMenu.right, top: downloadMenu.top, zIndex: 21,
                                        minWidth: 280, padding: 4,
                                        display: "flex", flexDirection: "column", alignItems: "stretch",
                                        background: "var(--color-surface, #ffffff)",
                                        border: "1px solid var(--color-border, #e5e7eb)",
                                        borderRadius: 8, boxShadow: "var(--shadow-md, 0 4px 12px rgba(0, 0, 0, 0.12))",
                                    }}>
                                        {SLOT_LABEL_STYLES.map(s => (
                                            <button key={s} type="button" role="menuitem"
                                                className="btn btn-ghost btn-sm"
                                                style={{ justifyContent: "flex-start", textAlign: "left", whiteSpace: "nowrap" }}
                                                onClick={() => void handleDownloadBay(bay, s)}>
                                                {t(`sorting_label_style_${s}`)}
                                            </button>
                                        ))}
                                    </div>
                                </>)}
                            </span>
                            <button className="btn btn-ghost btn-sm" title={t("print_all")} aria-label={t("print_all")}
                                disabled={bay.locations.length === 0}
                                onClick={() => setPrintBay(bay)}>
                                <Printer size={14} />
                            </button>
                            {hasEditableGrid && (
                                <button className="btn btn-ghost btn-sm" title={t("pas_duplicate_bay")} onClick={() => { setDuplicating(bay); setDuplicateValue(`${bay.name} (copy)`); }}>
                                    <Copy size={14} /> {t("pas_duplicate")}
                                </button>
                            )}
                            <button className="btn btn-ghost btn-sm dispatch-admin-row-icon--danger" onClick={() => setRemoveConfirm(bay)}>
                                <Trash2 size={14} />
                            </button>
                        </div>
                    </div>

                    {editingLightBayId === bay.bay_id && (
                        <div style={{ marginTop: 12, padding: 12, border: "1px dashed var(--color-border, #e5e7eb)", borderRadius: 8 }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
                                <input type="checkbox" checked={lightDraft.light_enabled}
                                    onChange={e => setLightDraft(d => ({ ...d, light_enabled: e.target.checked }))} />
                                {t("pas_light_enable")}
                            </label>
                            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
                                <label style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12 }}>
                                    {t("pas_artnet_host")}
                                    <input type="text" placeholder="192.168.1.50" value={lightDraft.artnet_host}
                                        style={{ width: 150 }}
                                        onChange={e => setLightDraft(d => ({ ...d, artnet_host: e.target.value }))} />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12 }}>
                                    {t("pas_artnet_universe")}
                                    <input type="number" min={0} value={lightDraft.artnet_universe}
                                        style={{ width: 90 }}
                                        onChange={e => setLightDraft(d => ({ ...d, artnet_universe: Number(e.target.value) || 0 }))} />
                                </label>
                                <label style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 12 }}>
                                    {t("pas_dmx_start")}
                                    <input type="number" min={1} max={512} value={lightDraft.dmx_start_channel}
                                        style={{ width: 90 }}
                                        onChange={e => setLightDraft(d => ({ ...d, dmx_start_channel: Number(e.target.value) || 1 }))} />
                                </label>
                                <SaveStatus dirty={lightDirty} saving={savingLight} />
                            </div>
                            <div style={{ fontSize: 12, color: "var(--color-text-muted, #6b7280)", marginTop: 8 }}>{t("pas_light_hint")}</div>
                        </div>
                    )}

                    {editingBayId === bay.bay_id && (
                        <div style={{ marginTop: 12 }}>
                            {isPas && !isPutWallBay(bay) && (
                                // Which fill order the priority inputs edit: Small
                                // (per-bay, today's) or Large (numbered across BOTH
                                // bays for double-bay waves). Not for the put wall
                                // — it is never part of the Large bay pair.
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                                    <div className="pas-priority-mode-toggle" role="radiogroup" aria-label={t("pas_priority")}>
                                        <button
                                            type="button" role="radio" aria-checked={priorityMode === "small"}
                                            className={`pas-priority-mode-pill${priorityMode === "small" ? " pas-priority-mode-pill--active" : ""}`}
                                            onClick={() => setPriorityMode("small")}
                                        >
                                            {t("pas_priority_mode_small")}
                                        </button>
                                        <button
                                            type="button" role="radio" aria-checked={priorityMode === "large"}
                                            className={`pas-priority-mode-pill${priorityMode === "large" ? " pas-priority-mode-pill--active" : ""}`}
                                            onClick={() => setPriorityMode("large")}
                                        >
                                            {t("pas_priority_mode_large")}
                                        </button>
                                    </div>
                                    {priorityMode === "large" && (
                                        <span style={{ fontSize: 12, color: "var(--color-text-muted, #6b7280)" }}>
                                            {t("pas_priority_cross_bay_hint")}
                                        </span>
                                    )}
                                </div>
                            )}
                            {seedingGrid && <div className="admin-settings-loading">{t("loading")}</div>}
                            {!seedingGrid && !hasEditableGrid && sorterLocs.length === 0 && (
                                <div className="admin-settings-empty">{t("sorter_no_locations")}</div>
                            )}
                            {!seedingGrid && (hasEditableGrid || sorterLocs.length > 0) && (
                                <div className="pas-grid-scroll">
                                    <table className="pas-grid">
                                        <thead>
                                            <tr>
                                                <th className="pas-grid-shelf" />
                                                {(hasEditableGrid ? [...positionsForBay(bay)] : sorterPositions).map((p, i, arr) => (
                                                    <th key={p} className={groupEndClass(i, arr.length)}>
                                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                            <span style={{ width: 12, height: 12, borderRadius: 3, background: palette.position[positionColourKey(p)], display: "inline-block" }} />
                                                            {p}
                                                        </span>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(hasEditableGrid ? [...SHELVES].reverse() : [...sorterShelves].reverse()).map(shelf => (
                                                <tr key={shelf}>
                                                    <th className="pas-grid-shelf" scope="row">
                                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                            <span style={{ width: 12, height: 12, borderRadius: 3, background: palette.shelf[shelf], display: "inline-block" }} />
                                                            {shelf}
                                                        </span>
                                                    </th>
                                                    {(hasEditableGrid ? [...positionsForBay(bay)] : sorterPositions).map((position, i, arr) => {
                                                        const key = hasEditableGrid ? cellKey(shelf, position) : sorterBarcodeAt(shelf, position);
                                                        const cell = key ? grid[key] : undefined;
                                                        const edge = groupEndClass(i, arr.length);
                                                        if (!key || !cell) return <td key={position} className={`pas-grid-cell--off${edge}`} />;
                                                        return (
                                                            <td key={position} className={`${cell.enabled ? "" : "pas-grid-cell--off"}${edge}`}>
                                                                {renderCellControls(key, cell, !!bay.light_enabled)}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <div style={{ fontSize: 12, color: "var(--color-text-muted, #6b7280)", marginTop: 6 }}>
                                {bay.light_enabled ? t("pas_aim_grid_hint") : t("pas_grid_hint")}
                            </div>
                            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                                <SaveStatus dirty={gridDirty} saving={savingGrid} />
                                {bay.light_enabled && (
                                    <button className="btn btn-ghost btn-sm" disabled={!aimingKey} onClick={() => lightsOff(bay.bay_id)} title={t("pas_lights_off")}>
                                        <Power size={14} /> {t("pas_lights_off")}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ))}

            {printBay && (
                <SortingLabelPrintModal
                    warehouse={warehouse}
                    bayName={printBay.name}
                    labels={printBay.locations.map(l => ({
                        barcode: l.barcode, label: l.label, shelf: l.shelf, position: l.position,
                        shelf_colour: l.shelf_colour ?? palette.shelf[l.shelf],
                        position_colour: l.position_colour ?? palette.position[positionColourKey(l.position)],
                    }))}
                    widthMm={labelSize.w}
                    heightMm={labelSize.h}
                    onSaveSize={saveLabelSize}
                    onClose={() => setPrintBay(null)}
                />
            )}

            {adding && (
                <div className="modal-overlay" onClick={() => !submitting && setAdding(false)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
                        <div className="modal-card-header">
                            <h2>{t("pas_add_bay")} — {warehouse}</h2>
                            <button className="modal-close" onClick={() => setAdding(false)} disabled={submitting}><X size={18} /></button>
                        </div>
                        <div className="modal-card-body">
                            <label className="dispatch-admin-form-label">
                                {t("pas_bay_name")}
                                <input type="text" autoFocus className="dispatch-admin-form-input" value={newName}
                                    onChange={e => setNewName(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter") handleAdd(); }} placeholder="Bay 1" />
                            </label>
                            {isSorter2 && (
                                <label className="dispatch-admin-form-label" style={{ marginTop: 12 }}>
                                    {t("sorter_bay_no_label")}
                                    <input type="number" min={1} max={99} className="dispatch-admin-form-input" value={newBayNo}
                                        onChange={e => setNewBayNo(e.target.value)} placeholder="1" />
                                </label>
                            )}
                            {isPas && (
                                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 12, cursor: "pointer" }}>
                                    <input type="checkbox" checked={newPutWall} onChange={e => setNewPutWall(e.target.checked)}
                                        style={{ marginTop: 3 }} />
                                    <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <span style={{ fontSize: 14, fontWeight: 600 }}>{t("pas_add_bay_put_wall")}</span>
                                        <span style={{ fontSize: 12, color: "var(--color-text-muted, #6b7280)" }}>{t("pas_add_bay_put_wall_hint")}</span>
                                    </span>
                                </label>
                            )}
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost" onClick={() => setAdding(false)} disabled={submitting}>{t("cancel")}</button>
                                <button className="btn btn-success" onClick={handleAdd} disabled={submitting || !newName.trim()}>
                                    {submitting ? t("submitting") : t("pas_add_bay")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {renaming && (
                <div className="modal-overlay" onClick={() => !submitting && setRenaming(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
                        <div className="modal-card-header">
                            <h2>{t("pas_rename_bay")}</h2>
                            <button className="modal-close" onClick={() => setRenaming(null)} disabled={submitting}><X size={18} /></button>
                        </div>
                        <div className="modal-card-body">
                            <label className="dispatch-admin-form-label">
                                {t("pas_bay_name")}
                                <input type="text" autoFocus className="dispatch-admin-form-input" value={renameValue}
                                    onChange={e => setRenameValue(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter") handleRename(); }} />
                            </label>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost" onClick={() => setRenaming(null)} disabled={submitting}>{t("cancel")}</button>
                                <button className="btn btn-success" onClick={handleRename} disabled={submitting || !renameValue.trim()}>
                                    {submitting ? t("submitting") : t("save")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {duplicating && (
                <div className="modal-overlay" onClick={() => !submitting && setDuplicating(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 360 }}>
                        <div className="modal-card-header">
                            <h2>{t("pas_duplicate_bay")}</h2>
                            <button className="modal-close" onClick={() => setDuplicating(null)} disabled={submitting}><X size={18} /></button>
                        </div>
                        <div className="modal-card-body">
                            <label className="dispatch-admin-form-label">
                                {t("pas_bay_name")}
                                <input type="text" autoFocus className="dispatch-admin-form-input" value={duplicateValue}
                                    onChange={e => setDuplicateValue(e.target.value)}
                                    onKeyDown={e => { if (e.key === "Enter") handleDuplicate(); }} />
                            </label>
                            <div className="modal-confirm-buttons" style={{ marginTop: 16 }}>
                                <button className="btn btn-ghost" onClick={() => setDuplicating(null)} disabled={submitting}>{t("cancel")}</button>
                                <button className="btn btn-success" onClick={handleDuplicate} disabled={submitting || !duplicateValue.trim()}>
                                    {submitting ? t("submitting") : t("pas_duplicate")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {removeConfirm && (
                <div className="modal-overlay" onClick={() => !submitting && setRemoveConfirm(null)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
                        <div className="modal-card-header">
                            <h2>{t("pas_remove_bay")}</h2>
                            <button className="modal-close" onClick={() => setRemoveConfirm(null)} disabled={submitting}><X size={18} /></button>
                        </div>
                        <div className="modal-card-body">
                            <p style={{ marginBottom: 16, lineHeight: 1.5 }}>{t("pas_confirm_remove_bay", { name: removeConfirm.name })}</p>
                            <div className="modal-confirm-buttons">
                                <button className="btn btn-ghost" onClick={() => setRemoveConfirm(null)} disabled={submitting}>{t("cancel")}</button>
                                <button className="btn btn-danger" onClick={confirmRemove} disabled={submitting}>{submitting ? t("submitting") : t("pas_remove_bay")}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SortingBayManager;
